/**
 * ``AnnotationRenderLayer`` — the Meridian-owned MapLibre layers that **display**
 * persisted annotations (ADR-034 design update PR D — the full Meridian render
 * path). Terra Draw now only *draws*; this owns the static render so we control
 * everything, including the per-shape **dash** style.
 *
 * Layers (bottom → top): a **fill** layer (circle + polygon), one **outline**
 * line layer **per dash pattern** (MapLibre ``line-dasharray`` isn't
 * data-driven, so solid/dashed/dotted/dash-dot each get a filtered layer), and a
 * **marker** circle layer. Colours/widths are data-driven from the feature
 * props (built by :mod:`@/annotations/annotation-map-features`, which also
 * resolves the selection highlight). The label layer mounts after this, on top.
 *
 * Every feature carries ``annotation_id``, so the map-click selection
 * (``queryRenderedFeatures`` in use-annotation-draw) still finds annotations now
 * that they're no longer Terra Draw features.
 *
 * Imperative attach/setData/detach like the other layers, ``isMapUsable``-guarded
 * (fail-safe; CLAUDE.md §8.1). WebGL-only — verified in the deferred §5.11 pass;
 * the feature builders it composes are unit-tested.
 *
 * Cross-references:
 *   - ADR-034 design update; feature-spec §5.2 / §10. REQ-UI-035 (BFF-only).
 *   - @/annotations/annotation-map-features (the pure source builders).
 */
import { useEffect, useMemo } from "react";
import type { GeoJSONSource, LineLayerSpecification, Map as MapLibreMap } from "maplibre-gl";
import type { FeatureCollection, LineString, Point, Polygon } from "geojson";
import { isMapUsable } from "@/map/map-ready";
import { MARKER_PIN_ICON_ID, registerMarkerIcon } from "@/map/annotation-marker-icon";
import { useAnnotationsWithDrag } from "@/annotations/annotation-drag-store";
import { useAnnotationToolStore } from "@/annotations/annotation-tool-store";
import { usePreferences } from "@/preferences/preferences-context";
import {
  DASH_LAYERS,
  annotationsToArrowFeatures,
  annotationsToFillFeatures,
  annotationsToMarkerFeatures,
  annotationsToOutlineFeatures,
  annotationsToRadiusFeatures,
} from "@/annotations/annotation-map-features";

const SOURCE_FILL = "meridian-annotation-fill-source";
const SOURCE_OUTLINE = "meridian-annotation-outline-source";
const SOURCE_MARKER = "meridian-annotation-marker-source";
const SOURCE_ARROW = "meridian-annotation-arrow-source";
const LAYER_FILL = "meridian-annotation-fill-layer";
const LAYER_MARKER = "meridian-annotation-marker-layer";
const LAYER_ARROW = "meridian-annotation-arrow-layer";
const SOURCE_RADIUS = "meridian-annotation-radius-source";
const LAYER_RADIUS = "meridian-annotation-radius-layer";
const LAYER_RADIUS_LABEL = "meridian-annotation-radius-label-layer";
const outlineLayerId = (dash: string): string => `meridian-annotation-outline-${dash}-layer`;

interface Sources {
  readonly fill: FeatureCollection<Polygon>;
  readonly outline: FeatureCollection<LineString>;
  readonly marker: FeatureCollection<Point>;
  readonly radius: FeatureCollection<LineString>;
}

/** An empty Polygon source — the arrow source's initial data; the zoom-aware
 *  effect fills it (its size depends on the current zoom, not available here). */
const EMPTY_POLYGON_FC: FeatureCollection<Polygon> = { type: "FeatureCollection", features: [] };

/** Attach the sources + layers on first call; ``setData`` thereafter. */
function attach(map: MapLibreMap, sources: Sources): void {
  if (!isMapUsable(map)) {
    return;
  }
  registerMarkerIcon(map); // idempotent — ensures the marker location-pin icon exists
  const existing = map.getSource(SOURCE_FILL);
  if (existing !== undefined) {
    (map.getSource(SOURCE_FILL) as GeoJSONSource).setData(sources.fill);
    (map.getSource(SOURCE_OUTLINE) as GeoJSONSource).setData(sources.outline);
    (map.getSource(SOURCE_MARKER) as GeoJSONSource).setData(sources.marker);
    (map.getSource(SOURCE_RADIUS) as GeoJSONSource).setData(sources.radius);
    // The arrow source is kept in sync by a separate zoom-aware effect (its head
    // size recomputes on every ``zoom`` — a fixed pixel size), not here.
    return;
  }
  map.addSource(SOURCE_FILL, { type: "geojson", data: sources.fill });
  map.addSource(SOURCE_OUTLINE, { type: "geojson", data: sources.outline });
  map.addSource(SOURCE_MARKER, { type: "geojson", data: sources.marker });
  map.addSource(SOURCE_ARROW, { type: "geojson", data: EMPTY_POLYGON_FC });
  map.addSource(SOURCE_RADIUS, { type: "geojson", data: sources.radius });

  map.addLayer({
    id: LAYER_FILL,
    type: "fill",
    source: SOURCE_FILL,
    paint: { "fill-color": ["get", "fill_color"], "fill-opacity": ["get", "fill_opacity"] },
  });

  // One outline layer per dash pattern (line-dasharray isn't data-driven).
  for (const { dash, dasharray, cap } of DASH_LAYERS) {
    const layer: LineLayerSpecification = {
      id: outlineLayerId(dash),
      type: "line",
      source: SOURCE_OUTLINE,
      filter: ["==", ["get", "stroke_dash"], dash],
      layout: { "line-cap": cap, "line-join": "round" },
      paint: { "line-color": ["get", "stroke_color"], "line-width": ["get", "stroke_width"] },
    };
    if (dasharray !== null) {
      layer.paint = { ...layer.paint, "line-dasharray": [...dasharray] };
    }
    map.addLayer(layer);
  }

  // Markers render as a location pin (#2) — an SDF symbol whose tip sits on the
  // marker point (icon-anchor "bottom"), tinted by the operator's marker colour
  // and sized from marker_size.
  map.addLayer({
    id: LAYER_MARKER,
    type: "symbol",
    source: SOURCE_MARKER,
    layout: {
      "icon-image": MARKER_PIN_ICON_ID,
      "icon-anchor": "bottom",
      "icon-size": ["/", ["get", "marker_size"], 12],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
    paint: { "icon-color": ["get", "marker_color"] },
  });

  // Arrowheads at line ends — a filled-triangle **fill** layer (one ground-
  // geometry Polygon per endpoint from annotationsToArrowFeatures), tinted by the
  // stroke colour. Ground geometry → the head scales with zoom like the line,
  // rather than the prior fixed-size SDF symbol (ADR-034 batch 6 #2/#3).
  map.addLayer({
    id: LAYER_ARROW,
    type: "fill",
    source: SOURCE_ARROW,
    paint: { "fill-color": ["get", "arrow_color"], "fill-opacity": 1 },
  });

  // The circle radius line (REQ-UI-134) — dotted, in the circle's own stroke
  // colour so it reads as part of the shape rather than as separate furniture.
  // Deliberately thinner than the circle outline and always dotted regardless
  // of the shape's dash style: it is an annotation ON the shape, not part of
  // its perimeter, and matching the outline's dash would blur that distinction.
  map.addLayer({
    id: LAYER_RADIUS,
    type: "line",
    source: SOURCE_RADIUS,
    layout: { "line-cap": "round" },
    paint: {
      "line-color": ["get", "stroke_color"],
      "line-width": 1.5,
      "line-dasharray": [0, 2],
    },
  });

  // The radius measurement, placed at the LINE CENTRE by MapLibre itself
  // (symbol-placement "line-center") rather than by a hand-computed anchor
  // point — so the text stays on the line as the map rotates or the circle is
  // resized, with no second source to keep in sync.
  map.addLayer({
    id: LAYER_RADIUS_LABEL,
    type: "symbol",
    source: SOURCE_RADIUS,
    layout: {
      "symbol-placement": "line-center",
      "text-field": ["get", "radius_text"],
      // The BFF glyph atlas fontstack (issue #205) — same stack the annotation
      // label layer uses, so the radius reads as the same typographic family.
      "text-font": ["DM Sans Medium"],
      "text-size": 12,
      "text-offset": [0, -0.8],
      // Always render, matching the label layer's #14 fix: repeated setData
      // (every unit switch re-formats this text) can leave symbol
      // collision/fade state hiding the label permanently. Overlap is
      // acceptable for operator annotations.
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: {
      "text-color": ["get", "stroke_color"],
      // Dark halo so the radius reads on any basemap, as for the shape labels.
      "text-halo-color": "#0f1117",
      "text-halo-width": 1.5,
    },
  });
}

/** Remove the layers + sources. */
function detach(map: MapLibreMap): void {
  if (!isMapUsable(map)) {
    return;
  }
  for (const { dash } of DASH_LAYERS) {
    if (map.getLayer(outlineLayerId(dash)) !== undefined) {
      map.removeLayer(outlineLayerId(dash));
    }
  }
  for (const id of [LAYER_FILL, LAYER_MARKER, LAYER_ARROW, LAYER_RADIUS, LAYER_RADIUS_LABEL]) {
    if (map.getLayer(id) !== undefined) {
      map.removeLayer(id);
    }
  }
  for (const id of [SOURCE_FILL, SOURCE_OUTLINE, SOURCE_MARKER, SOURCE_ARROW, SOURCE_RADIUS]) {
    if (map.getSource(id) !== undefined) {
      map.removeSource(id);
    }
  }
}

/** Render the persisted annotations. Mount with the loaded map; renders null. */
export function AnnotationRenderLayer({ map }: { map: MapLibreMap }): null {
  const data = useAnnotationsWithDrag();
  const selectedId = useAnnotationToolStore((state) => state.selectedId);
  const annotations = useMemo(() => data ?? [], [data]);
  // The radius label is formatted at source-build time, so the source rebuilds
  // when the active Profile's distance unit changes (same treatment as the
  // annotation label layer).
  const distanceUnit = usePreferences().workingState.units?.distance ?? "nautical_miles";

  const sources = useMemo<Sources>(
    () => ({
      fill: annotationsToFillFeatures(annotations),
      outline: annotationsToOutlineFeatures(annotations, selectedId),
      marker: annotationsToMarkerFeatures(annotations, selectedId),
      radius: annotationsToRadiusFeatures(annotations, distanceUnit),
    }),
    [annotations, selectedId, distanceUnit],
  );

  useEffect(() => () => detach(map), [map]);
  useEffect(() => {
    attach(map, sources);
  }, [map, sources]);

  // Arrowheads are a FIXED PIXEL SIZE: their ground geometry is recomputed from
  // the CURRENT zoom (annotationsToArrowFeatures(_, map.getZoom())) on every
  // ``zoom`` event + whenever the annotations change — so the head stays the same
  // size in pixels as you zoom and the same size regardless of line length (like
  // the marker; operator request 2026-06-12). Separate from the static sources
  // above so the line/shapes don't re-setData on every zoom frame.
  useEffect(() => {
    const setArrows = (): void => {
      if (!isMapUsable(map)) {
        return;
      }
      const source = map.getSource(SOURCE_ARROW) as GeoJSONSource | undefined;
      if (source !== undefined) {
        source.setData(annotationsToArrowFeatures(annotations, map.getZoom()));
      }
    };
    setArrows();
    map.on("zoom", setArrows);
    return () => {
      map.off("zoom", setArrows);
    };
  }, [map, annotations]);

  return null;
}
